import Table from "@/common/elements/Table";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../InmateDetails/styles";
import Button from "@/common/elements/Button";
import { StyledFlex, StyledTypographyText } from "@/common/styles";
import { useEffect, useRef, useState } from "react";
import CustomModal from "../CustomModal";
import BailRecordDetailForm from "./BailRecordDetailForm";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidV4 } from "uuid";
import {
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import { Form, Popconfirm } from "antd";
import dayjs from "dayjs";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import _ from "lodash";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const BAIL_RECORDS_HEADERS = [
  { key: "id", text: "ID" },
  { key: "dateAdded", text: "Date Added" },
  { key: "timeAdded", text: "Time Added" },
  { key: "bailAmount", text: "Bail Amount" },
  { key: "courtNumber", text: "Court #" },
  { key: "bailType", text: "Bail Type" },
  { key: "paidLabel", text: "Satisfied" },
  { key: "comments", text: "Comments" },
];

const ChargesBailRecordsDetails = ({startingPoint}) => {
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: bailRecordsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:BookingBails", BAIL_RECORDS_HEADERS);

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [bookingBails, setBookingBails] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [totalUnpaidBill, setTotalUnpaidBill] = useState(0);
  const [selectBail, setSelectBail] = useState(null);
  const [isEdit, setisEdit] = useState(false);

  // BookingBailsExtra field keys/type map/current PK, reported up from
  // BailRecordDetailForm — see the onExtraMetaChange prop passed to it
  // below. Read at save time, not a render input, so a ref is enough.
  const bailExtraMetaRef = useRef({ fieldKeys: [], typeMap: {}, extraID: 0 });

  useEffect(() => {
    if (bookings !== null) {
      const bookingBails = [];
      let totalUnpaidBill = 0;
      console.log(bookings?.bookingBails);

      bookings?.bookingBails?.forEach((element, index) => {
        if (!element?.paid) {
          if (isNonEmptyVariable(element?.bailAmount)) {
            totalUnpaidBill += parseFloat(element?.bailAmount);
          }
        }
        bookingBails.push({
          ...element,
          id: index + 1,
          bookingBailUuid: element?.bookingBailUuid || uuidV4(),
          bailType: isNonEmptyVariable(element?.bailTypeID)
            ? getRefListNameByID("BondType", element?.bailTypeID)
            : "",
          paidLabel:
            isNonEmptyVariable(element?.paid) && element?.paid
              ? "True"
              : "False",
        });
      });
      setTotalUnpaidBill(() => totalUnpaidBill);
      setBookingBails(() => bookingBails);
    }
  }, [bookings]);

  const handleModalVisble = () => {
    setIsModalVisible(!isModalVisible);
  };

  const modifyBailDetailsAction = () => {
    form.validateFields().then((v) => {
      // Split BookingBailsExtra dynamic fields out of the form values —
      // they're not part of the main bail record, and (unlike Charges)
      // there's no dedicated save API here at all: this whole record only
      // ever gets persisted later, as part of the main booking save. So the
      // extra fields just get nested onto bookingBailsExtra[0] here, same
      // place Bio/ArrestReports/InmateProperty already stash theirs, and
      // saveBookingBailsExtra (called from the main booking save success
      // handler) picks them up from there.
      const { fieldKeys, typeMap, extraID } = bailExtraMetaRef.current;
      const extraFieldsData = {};
      fieldKeys.forEach((k) => {
        const val = v?.[k];
        const htmlType = typeMap[k];
        if (val) {
          extraFieldsData[k] =
            htmlType === "date" ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime" ? dayjs(val).format("MM/DD/YYYY HH:mm")
            : val;
        } else {
          extraFieldsData[k] = null;
        }
        delete v[k];
      });

      const updateBookingBails = bookingBails?.map((bb) => {
        if (
          (isNonEmptyVariable(bb?.bookingBailID) &&
            bb?.bookingBailID === selectBail?.bookingBailID) ||
          bb?.bookingBailUuid === selectBail?.bookingBailUuid
        ) {
          console.log(bb);
          const existingExtra = bb?.bookingBailsExtra?.[0] || {};
          return {
            ...bb,
            ...v,
            offBondDate: isNonEmptyVariable(v?.offBondDate)
              ? dayjs(v?.offBondDate)?.format("MM/DD/YYYY")
              : null,
            bookingBailsExtra:
              fieldKeys.length > 0
                ? [
                    {
                      ...existingExtra,
                      ...extraFieldsData,
                      bookingBailsExtraID:
                        existingExtra?.bookingBailsExtraID ?? extraID ?? 0,
                      bookingBailID:
                        existingExtra?.bookingBailID ??
                        bb?.bookingBailID ??
                        0,
                    },
                  ]
                : bb?.bookingBailsExtra,
          };
        }
        return bb;
      });

      dispatch(
        setBookings({
          bookingBails: updateBookingBails,
        })
      );

      cancelAction();
    });
  };

  const onRowClick = (data) => {
    setSelectBail(data);
    setisEdit(() => true);
  };

  const cancelAction = () => {
    setSelectBail(null);
    setisEdit(() => false);
    setIsModalVisible(() => false);
  };

  const deleteBail = () => {
    let data = _.cloneDeep(bookingBails);
    data = data?.filter((d) => {
      return d?.bookingBailUuid !== selectBail?.bookingBailUuid;
    });

    let bookingCharges = bookings.charges.map((charge) => {
      const bail = charge.bookingBails?.[0];
      return {
        ...charge,
        bookingBails:
          bail?.bookingBailID === selectBail?.bookingBailID ? [] : bail,
      };
    });

    dispatch(
      setBookings({
        charges: bookingCharges,
        bookingBails: data,
      })
    );

    const url = API_ENDPOINTS.bookingBails + "/id/" + selectBail?.bookingBailID;
    dispatch(setDeleteAPis(url));
    cancelAction();
  };

  return (
    <>
      <InmateDetailsTableContainer>
        <TableHeader>
          <h6>List of Bail Records</h6>
          <div className="right">
            {
              <Button
                onClick={handleModalVisble}
                disabled={
                  !isEdit ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.VIEW,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report
                  )
                }
              >
                Details
              </Button>
            }

            {
              (startingPoint != "FolderSearch") && (
              <Popconfirm
                title="Delete the Bail"
                description="Are you sure to delete this bail record?"
                onConfirm={deleteBail}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  style={{ marginLeft: "10px" }}
                  color="primary"
                  type="primary"
                  disabled={
                    !isEdit ||
                    !bookingsFieldsEnabled ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.DELETE,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  DELETE
                </Button>
              </Popconfirm>)
            }
            <Button
              style={{ marginLeft: "10px" }}
              disabled={!isEdit || isConfiguring}
              onClick={cancelAction}
            >
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                style={{ marginLeft: "10px" }}
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  style={{ marginLeft: "10px" }}
                  htmlType="button"
                >
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </div>
        </TableHeader>

        {hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.VIEW,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report
        ) && (
          <Table
            ref={dataTableRef}
            headers={bailRecordsHeaders}
            draggable={isConfiguring}
            data={bookingBails}
            // variant="outlined"
            onRowClick={onRowClick}
            onRowDoubleClick={(value) => {
              onRowClick(value);
              handleModalVisble();
            }}
            selected={selectBail === null}
          />
        )}
        <StyledFlex justify="end">
          <StyledTypographyText
            textTransform="capitalize"
            fontSize="15px"
            color="#000"
          >
            Total Unpaid Bail: <span>{totalUnpaidBill}</span>
          </StyledTypographyText>
        </StyledFlex>
      </InmateDetailsTableContainer>

      {isModalVisible && (
        <CustomModal
          show={isModalVisible}
          onHide={handleModalVisble}
          size="xl"
          header="Bail Details"
          onSaveClick={modifyBailDetailsAction}
          isDisabled={
            !bookingsFieldsEnabled ||
            hasCheckSecurity(
            Security_Types.functionality,
            Entity_types.subModule,
            LABELS_CAPITAL.MODIFY,
            MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
            SUB_MODULES_LIST_WITH_ID.Arrest_Report
          )}
        >
          <BailRecordDetailForm
            form={form}
            formData={selectBail}
            startingPoint={startingPoint}
            onExtraMetaChange={(meta) => {
              bailExtraMetaRef.current = meta;
            }}
          />
        </CustomModal>
      )}
    </>
  );
};
export default ChargesBailRecordsDetails;
